// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.
import type { SemVer } from "./types.ts";
import {
  checkIdentifier,
  compareIdentifier,
  compareNumber,
} from "./_shared.ts";

/**
 * Compare two SemVers.
 *
 * Returns `0` if `version1` equals `version2`, or `1` if `version1` is greater, or `-1` if `version2` is
 * greater.
 *
 * Sorts in ascending order if passed to {@linkcode Array.sort}.
 *
 * @example Usage
 * ```ts
 * import { parse, compare } from "@std/semver";
 * import { assertEquals } from "@std/assert";
 *
 * const version1 = parse("1.2.3");
 * const version2 = parse("1.2.4");
 *
 * assertEquals(compare(version1, version2), -1);
 * assertEquals(compare(version2, version1), 1);
 * assertEquals(compare(version1, version1), 0);
 * ```
 *
 * @param version1 The first SemVer to compare
 * @param version2 The second SemVer to compare
 * @returns `1` if `version1` is greater, `0` if equal, or `-1` if `version2` is greater
 */
export function compare(version1: SemVer, version2: SemVer): 1 | 0 | -1 {
  if (version1 === version2) return 0;
  return (
    compareNumber(version1.major, version2.major) ||
    compareNumber(version1.minor, version2.minor) ||
    compareNumber(version1.patch, version2.patch) ||
    checkIdentifier(version1.prerelease, version2.prerelease) ||
    compareIdentifier(version1.prerelease, version2.prerelease)
  );
}

// denoCacheMetadata={"headers":{"via":"1.1 google","cache-control":"public, max-age=31536000, immutable","x-robots-tag":"noindex","x-goog-generation":"1725446501890653","content-length":"1431","expires":"Sat, 08 Nov 2025 09:36:15 GMT","etag":"\"af17d87c29f36cce83f800146bfb3fab\"","age":"2026","x-guploader-uploadid":"AHmUCY00ftD5v2hr5nm1DixsFDL1MonfQxSQ7rxtL2DcUz57JbUUUIeyMFGjh5iF2Yjws_A-EPY","last-modified":"Wed, 04 Sep 2024 10:41:41 GMT","x-jsr-cache-status":"hit","x-goog-storage-class":"STANDARD","date":"Fri, 08 Nov 2024 09:36:15 GMT","x-jsr-cache-id":"IAD","access-control-expose-headers":"*","x-goog-stored-content-length":"1431","accept-ranges":"bytes","x-goog-metageneration":"1","x-goog-stored-content-encoding":"identity","server":"UploadServer","x-goog-hash":"crc32c=6GFX2A==,md5=rxfYfCnzbM6D+AAUa/s/qw==","content-type":"text/typescript","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","access-control-allow-origin":"*","cross-origin-resource-policy":"cross-origin","x-content-type-options":"nosniff","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000"},"url":"https://jsr.io/@std/semver/1.0.3/compare.ts","time":1731060601}